import {createSelector} from '@reduxjs/toolkit'
import {QueryStatus} from '@reduxjs/toolkit/dist/query'
import {
  getAccommodationPageHotelId,
  getMetaBrandHasRooms,
  getRoomsActiveFilters,
  getRoomsTopOfferLock
} from 'modules/accommodation/selectors'
import {
  extractRegularOfferParams,
  extractSplitBookingParams,
  getApLandingRedirectId,
  getRoomsSearchParamsFromApUrl,
  isVioOfferOrBundleSelected
} from 'modules/accommodation/utils'
import {getVclidCookie} from 'modules/cookies/selectors'
import {
  getDeviceId,
  getDeviceLayout,
  getMeta,
  getUserCountryCode
} from 'modules/meta/selectors'
import {getAllOffersOverlay} from 'modules/overlay/selectors'
import {OfferAccessFlow} from 'modules/sapiSearch/constants'
import {
  getHotel,
  getHotelOfferEntityOffers,
  getIsAnchorHotel,
  getIsAppLockedDealAudience,
  getOfferAccessFlowSettings,
  getOptimizedRoomsConfig,
  getSearchId,
  getSearchParameters,
  getSearchParametersLabel,
  getShowTotalPrices,
  getSplitBookingDetails
} from 'modules/sapiSearch/selectors'
import {getMetadata} from 'modules/sapiSearch/utils'
import {
  getIsMultiRoomBundlesSearch,
  getIsOptimizeRooms,
  getIsStickyOtaMode,
  getLandingItinerary,
  getLandingTrafficSource,
  getOfferModeOverride,
  getUseBoVioFirstProfile,
  isLandingItinerary
} from 'modules/search/selectors'
import {getUrlParams} from 'modules/search/selectors'
import {getSearchBoxValues} from 'modules/searchBox/selectors'
// eslint-disable-next-line no-restricted-imports
import {isEmpty, omit} from 'ramda'
import {RootState} from 'store'
import {getHotelIdFromAccommodationPage} from 'utils/getHotelIdFromAccommodationPage'
import {getCheapestTopOfferByNightlyRate} from 'utils/offers'

// Direct import of selector module needed instead of useBrand hook in selector context where hooks cannot be used.
// eslint-disable-next-line @vio/custom-rules/prefer-usebrand-hook
import {getIsBrandOffersLockedByDefault} from '@vio/core/src/_web/brand/modules/selectors'
import {getPersistedSapiLabel} from '@vio/core/src/_web/sapiLabel/business'
import {getEmailDomain} from '@vio/core/src/auth/business/authentication'
import {
  getIsAuthenticated,
  getIsLockedDeal,
  getUser,
  getUserId,
  selectIsEmployee,
  selectIsMemberPlus,
  selectUserEmail
} from '@vio/core/src/auth/modules/selectors'
import {Placement} from '@vio/core/src/deepLinking/types'
import {calculateTotalRate} from '@vio/core/src/offer/business/calculateTotalRate'
import {getCugDealsParameter} from '@vio/core/src/offer/business/getCugDealsParameter'
import getIsBofhOffer from '@vio/core/src/offer/business/isBofhOffer'
import isBofhOffer from '@vio/core/src/offer/business/isBofhOffer'
import {hasOfferTag} from '@vio/core/src/offer/business/offers'
import {
  getGsiteValueFromUrl,
  getVfValueFromUrl
} from '@vio/core/src/offer/business/privateDeals'
import {OfferTagLabel} from '@vio/core/src/offer/types/offer'
import {getTotalPrice} from '@vio/core/src/price/business/price'
import {
  BOVIO_FIRST_PROFILE,
  OFFER_MODE_ORIGIN_ID,
  OTA_MODE_PROFILE
} from '@vio/core/src/profile/constants'
import {numberOfRooms as getNumberOfRooms} from '@vio/core/src/room/business/roomConfiguration'
import {
  applyRoomsFilterMemo,
  FilteredRoomsData
} from '@vio/core/src/room/business/utils/filters'
import {
  mapRoomToAbstract,
  mapSplitBookingToAbstract
} from '@vio/core/src/room/business/utils/mapToAbstract'
import {Room, RoomCardEntity} from '@vio/core/src/room/types/room'
import {
  GetOffersParams,
  GetRoomsParams,
  searchApi
} from '@vio/core/src/sapi/services/searchApi'
import {getUserTierForSapi} from '@vio/core/src/sapi/utils'
import {removeEmpty} from '@vio/core/src/utils/object'
import {parseQueryString} from '@vio/core/src/utils/url'
import {getHasILResidentVAT} from '@vio/shared/src/utils/PriceInformations/vat'

const getHotelIdParam = (_: unknown, hotelId: string) => hotelId
const getOfferIdParam = (_: unknown, offerId: string) => offerId
const getRoomIdParam = (_: unknown, roomId: string) => roomId
const getRoomIdParamAfterHotelId = (_: unknown, __: unknown, roomId: string) =>
  roomId

const getStateParam = (state: RootState) => state

export const getCommonSearchParams = createSelector(
  [
    selectIsMemberPlus,
    getUser,
    getIsAuthenticated,
    getDeviceId,
    getIsBrandOffersLockedByDefault,
    getUrlParams,
    selectIsEmployee,
    getIsAppLockedDealAudience,
    getSearchParametersLabel
  ],
  (
    isMemberPlus,
    user,
    isAuthenticated,
    deviceId,
    isBrandOffersLockedByDefault,
    urlParams,
    isEmployee,
    isAppLockedDealAudience,
    searchParametersLabel
  ) => {
    const userId = getUserId(user)
    const emailDomain = getEmailDomain(user?.attributes?.email)
    const cugDeals = getCugDealsParameter({
      isMemberPlus,
      isEmployee,
      urlParams,
      isBrandOffersLockedByDefault,
      isAppLockedDealAudience
    })
    const label = searchParametersLabel || getPersistedSapiLabel()
    const tier = getUserTierForSapi({
      isAuthenticated,
      isMemberPlus,
      urlParams,
      isBrandOffersLockedByDefault
    })

    return {
      tier,
      cugDeals,
      label,
      userId,
      emailDomain,
      deviceId
    }
  }
)

/**
 * Generate base parameters for the offers query
 * Params are used to generate the cache key that is also used by selectors so don't add/change params externally
 */
export const getOffersBaseParameters = createSelector(
  [
    getHotelIdParam,
    getUrlParams,
    getCommonSearchParams,
    getSearchParameters,
    getIsAnchorHotel,
    getMeta,
    getIsMultiRoomBundlesSearch,
    getIsOptimizeRooms,
    getVclidCookie,
    getUseBoVioFirstProfile,
    getIsStickyOtaMode,
    getLandingItinerary,
    getOfferModeOverride
  ],
  (
    hotelId,
    urlParams,
    commonSearchParams,
    searchParameters,
    isAnchor,
    meta,
    isMultiRoomBundlesSearch,
    isOptimizeRooms,
    vclidCookie,
    boVioFirstProfileIsEnabled,
    isStickyOtaMode,
    landingItinerary,
    offerModeOverride
  ): GetOffersParams => {
    const {
      rooms,
      checkIn,
      checkOut,
      preferredRate,
      metadata,
      optimizeAnchorRooms,
      sbc
    } = searchParameters || {}
    const {includeTaxes, includeLocalTaxes} = meta
    const {profile: originId} = urlParams || {}
    const numberOfRooms = getNumberOfRooms(rooms)
    const optimizeRooms = isAnchor ? optimizeAnchorRooms : isOptimizeRooms
    // The meta landing token goes with the landing hotel and dates, not with
    // the anchor: a standalone AP landing never sets one.
    const canCarryLandingToken = isLandingItinerary(
      {hotelId, checkIn, checkOut},
      landingItinerary
    )
    return {
      ...commonSearchParams,
      shouldDispatchActions: true, // Enable tracking & syncing default dates w. searchbox
      rooms,
      checkIn,
      checkOut,
      originId: offerModeOverride
        ? OFFER_MODE_ORIGIN_ID[offerModeOverride]
        : originId ||
          (boVioFirstProfileIsEnabled
            ? BOVIO_FIRST_PROFILE
            : isStickyOtaMode
              ? OTA_MODE_PROFILE
              : undefined),
      includeTaxes,
      includeLocalTaxes,
      numberOfRooms,
      includeRoomsInNightlyPrice: isMultiRoomBundlesSearch,
      optimizeRooms,
      isAnchor,
      hotelId,
      sbc,
      metadata: canCarryLandingToken
        ? metadata
        : getMetadata(undefined, undefined, vclidCookie),
      // preferred_rate tag related params only provided if anchor hotel is selected
      ...(isAnchor && {preferredRate}),
      getAllOffers: true
    }
  }
)

/**
 * Generate parameters for the top offers query
 * Params are used to generate the cache key that is also used by selectors so don't add/change params externally
 */
export const getTopOffersParameters = createSelector(
  [getOffersBaseParameters, getUrlParams],
  (baseOffersParams, urlParams): GetOffersParams => {
    const {freeCancellation, amenities, payLater} = urlParams
    const filters = {
      ...(amenities && {amenities}),
      ...(freeCancellation && {freeCancellation: freeCancellation === '1'}),
      ...(payLater && {payLater: payLater === '1'})
    }

    return {
      ...baseOffersParams,
      shouldUpdateOfferEntities: true,
      ...(!isEmpty(filters) && {filters})
    }
  }
)

/**
 * Get clickedOffer data from the cheapest available bofh offer from hotel-offers entity
 * It may be a regular offer or split booing, depends on sbPosition in the displayed top-offers
 *
 * If there's no bofh offer available in hotel-offers entity - returns empty object
 */
export const getCheapestBofhTopOfferParams = createSelector(
  [getHotelOfferEntityOffers, getSplitBookingDetails],
  (
    offers = [],
    {
      splitBookingPosition: sbOfferIndex,
      splitBookingBundle,
      hasSplitBookingOffer
    }
  ) => {
    //skip adding params when vio offer has been selected
    if (isVioOfferOrBundleSelected()) return {}

    const cheapestRegularOfferIndex = offers.findIndex(getIsBofhOffer)
    const isSbCheapest =
      hasSplitBookingOffer && sbOfferIndex <= cheapestRegularOfferIndex

    //get cheapest regular offer
    const regularOffer = offers.find(getIsBofhOffer)

    const noBofhOfferAvailable = !hasSplitBookingOffer && !regularOffer

    //skip adding params when there is no bofh offer in top-offers
    if (noBofhOfferAvailable) return {}

    return isSbCheapest
      ? extractSplitBookingParams(splitBookingBundle)
      : extractRegularOfferParams(regularOffer)
  }
)
type UrlParams = ReturnType<typeof getUrlParams>

/**
 * Metadata of the rooms request, shared with the server preheat so both send the same value.
 */
const getRoomsMetadata = (
  urlParams: UrlParams | undefined,
  vclid: string,
  canCarryLandingToken: boolean
) => {
  const {epv, esd, preferredRate} = urlParams || {}
  return canCarryLandingToken
    ? getMetadata(esd, epv, vclid, preferredRate?.toString())
    : getMetadata(undefined, undefined, vclid)
}

/**
 * Rooms query parameters that don't depend on the top offers
 */
const getRoomsBaseParameters = createSelector(
  [
    getHotelIdParam,
    getSearchParameters,
    getCommonSearchParams,
    getVclidCookie,
    selectUserEmail,
    getUser,
    getOptimizedRoomsConfig,
    getUrlParams,
    getSearchBoxValues,
    getIsAnchorHotel,
    getIsStickyOtaMode,
    getLandingItinerary,
    getOfferModeOverride
  ],
  (
    hotelId,
    searchParams,
    commonSearchParams,
    vclidCookie,
    userEmail,
    user,
    rooms,
    urlParams,
    searchBoxValues,
    isAnchor,
    isStickyOtaMode,
    landingItinerary,
    offerModeOverride
  ) => {
    // We still need to read checkIn/checkOut from the URL here because in production
    // the Redux state may not be populated fast enough. Using the URL values as a fallback
    // avoids request failures when the store isn’t ready yet.
    const {checkIn: searchCheckIn, checkOut: searchCheckOut} =
      searchParams || {}
    const {checkIn, checkOut} = searchBoxValues
    const requestCheckIn = searchCheckIn || checkIn
    const requestCheckOut = searchCheckOut || checkOut

    const {cugDeals, tier, label} = commonSearchParams
    const emailDomain = getEmailDomain(userEmail)
    const userId = getUserId(user)
    const {profile: originId, bookingHint} = urlParams || {}
    // The meta landing token only works for the hotel and dates the user landed on
    const canCarryLandingToken = isLandingItinerary(
      {hotelId, checkIn: requestCheckIn, checkOut: requestCheckOut},
      landingItinerary
    )
    const metadata = getRoomsMetadata(
      urlParams,
      vclidCookie,
      canCarryLandingToken
    )
    return {
      tier,
      label,
      rooms,
      checkIn: requestCheckIn,
      checkOut: requestCheckOut,
      cugDeals: cugDeals.join(','),
      hotelId,
      metadata,
      emailDomain,
      userId,
      originId: offerModeOverride
        ? offerModeOverride === 'otaOnly'
          ? OTA_MODE_PROFILE
          : undefined
        : originId || (isStickyOtaMode ? OTA_MODE_PROFILE : undefined),
      isAnchor,
      ...getRoomsSearchParamsFromApUrl(),
      bookingHint
    }
  }
)

/**
 * Identifies the search the rooms request belongs to, ignoring the top offers
 */
export const getRoomsSearchKey = createSelector(
  [getRoomsBaseParameters],
  baseParams => JSON.stringify(baseParams)
)

/**
 * The clicked offer params locked for the current rooms search, if any
 */
const getLockedRoomsTopOfferParams = createSelector(
  [getRoomsTopOfferLock, getHotelIdParam, getRoomsSearchKey],
  (lock, hotelId, searchKey) =>
    lock && lock.hotelId === hotelId && lock.searchKey === searchKey
      ? lock.params
      : undefined
)

export const getHasRoomsTopOfferLock = (state: RootState, hotelId: string) =>
  Boolean(getLockedRoomsTopOfferParams(state, hotelId))

/**
 * Generate parameters for the rooms query
 * Params are used to generate the cache key that is also used by selectors so don't add/change params externally
 */
export const getHotelApiRoomsParameters = createSelector(
  [
    getRoomsBaseParameters,
    getCheapestBofhTopOfferParams,
    getLockedRoomsTopOfferParams
  ],
  (baseParams, hotelCardTopOfferParams, lockedTopOfferParams) => ({
    ...(lockedTopOfferParams ?? hotelCardTopOfferParams),
    ...baseParams
  })
)

/**
 * Generates sanitized parameters for the preheat rooms API call.
 * Removes URL-specific clicked offer parameters that are irrelevant for preheat requests.
 *
 * @param params - The full set of hotel API rooms parameters.
 * @returns A filtered params object excluding clicked offer related keys.
 */
export const getPreheatRoomsParams = createSelector(
  [getHotelApiRoomsParameters],
  params => omit(['bundleId', 'clickedOfferId'], params)
)

/**
 * Rooms preheat params for SSR, or undefined when the server can't know them.
 * The server has no auth state or cookie state, so the user fields and the `vclid` come
 * from the synced `searchParameters`.
 */
export const getServerPreheatRoomsParams = createSelector(
  [
    getPreheatRoomsParams,
    getSearchParameters,
    getUrlParams,
    getMetaBrandHasRooms,
    getIsOptimizeRooms,
    getHotelIdParam,
    getLandingItinerary
  ],
  (
    preheatParams,
    searchParameters,
    urlParams,
    brandHasRooms,
    isOptimizeRooms,
    hotelId,
    landingItinerary
  ) => {
    const {checkIn, checkOut, tier, cugDeals, userId, emailDomain, metadata} =
      searchParameters ?? {}
    const vclid = metadata?.vclid

    // With optimizeRooms, `rooms` waits for the top offers, which the server doesn't have.
    // Without a vclid the client mints one after mount, so the request can't match.
    if (!brandHasRooms || !checkIn || !checkOut || isOptimizeRooms || !vclid) {
      return undefined
    }

    return removeEmpty({
      ...preheatParams,
      tier,
      cugDeals: cugDeals?.join(','),
      userId,
      emailDomain,
      metadata: getRoomsMetadata(
        urlParams,
        vclid,
        isLandingItinerary({hotelId, checkIn, checkOut}, landingItinerary)
      )
    })
  }
)

/**
 * Select result from the RTKQ cache by the cache key for the current top offers request
 */
const getTopOffersResult = (state: RootState) => {
  const hotelId = getAccommodationPageHotelId(state)
  const params = getTopOffersParameters(state, hotelId)
  return searchApi.endpoints.getOffers.select(params)(state)
}

/**
 * Select result from the RTKQ cache by the cache key for the current rooms request
 */
export const getRoomsResult = (state: RootState) => {
  const hotelId = getAccommodationPageHotelId(state)
  const params = getHotelApiRoomsParameters(state, hotelId)

  return searchApi.endpoints.getRoomsPolling.select(params as GetRoomsParams)(
    state
  )
}

/**
 * Returns true when a refetch is in progress (polling interval or manual).
 * False during initial load - only true for subsequent refreshes.
 */
export const getIsRoomsRefreshing = createSelector(
  [getRoomsResult],
  result => result?.data?.status?.isRefreshing ?? false
)

const EMPTY_FILTER_RESULTS: Omit<FilteredRoomsData, 'hotelId' | 'anonymousId'> =
  {
    rooms: [],
    availableFilters: {},
    totalResults: undefined,
    isSplitBookingAvailable: false,
    splitBooking: undefined,
    searchId: undefined
  }
/**
 * Get rooms with filters applied for the current rooms request
 */
export const getFilteredRoomsData = createSelector(
  [getRoomsResult, getRoomsActiveFilters],
  (result, filters) =>
    result?.data
      ? applyRoomsFilterMemo(result.data, filters)
      : EMPTY_FILTER_RESULTS
)

/**
 * Filtered rooms and split booking as one list of room card entities, for
 * code that works on the whole rooms list. Split booking always comes first
 * (product decision).
 */
export const getRoomCardEntities = createSelector(
  [getFilteredRoomsData],
  ({rooms, splitBooking}): RoomCardEntity[] => [
    ...(splitBooking ? [mapSplitBookingToAbstract(splitBooking)] : []),
    ...rooms.map(mapRoomToAbstract)
  ]
)

const EMPTY_ROOMS: Room[] = []

/**
 * Rooms before roomFilters are applied. Lets consumers tell "hidden by the
 * active filter" apart from "absent from this search".
 */
export const getUnfilteredRooms = createSelector(
  [getRoomsResult],
  result => result?.data?.rooms ?? EMPTY_ROOMS
)

/**
 * The offer served in Structured Data Markup on the AP: the user's selection
 * (preferred_rate). The proactive offer check must fire on this exact offer so
 * the validated price matches the price emitted in SDM.
 */
export const getStructuredDataMarkupOffer = createSelector(
  [getFilteredRoomsData],
  filteredData => {
    for (const room of filteredData?.rooms ?? []) {
      for (const offer of room.offers) {
        if (hasOfferTag(offer, OfferTagLabel.IsYourChoice)) {
          const offerPrice = offer.prices?.[0]
          if (!offerPrice) continue
          return {room, offer, offerPrice}
        }
      }
    }
    return null
  }
)

/**
 * Cheapest regular room offer by total price (ignoring clicked/preferred priority).
 * Returns undefined when split booking is cheaper or no regular offers exist.
 */
export const getCheapestRegularOfferFromSortedEntities = createSelector(
  [getFilteredRoomsData],
  filteredData => {
    const {rooms = [], splitBooking} = filteredData || {}

    const allOffers = rooms.flatMap(room => room.offers)
    const sbPrice = getTotalPrice(splitBooking?.prices?.[0])

    if (!sbPrice) return allOffers[0]
    return allOffers.find(offer => getTotalPrice(offer.prices[0]) < sbPrice)
  }
)

/**
 * Get searchId for top offers (if available)
 */
const getTopOffersSearchId = (state: RootState) => {
  const {data} = getTopOffersResult(state)
  return data?.searchId
}

/**
 * Get searchId for currently visible offers
 */
export const getCurrentOffersSearchId = (state: RootState) => {
  const isAllOffersOpen = Boolean(getAllOffersOverlay(state))
  const isAccommodationPage = Boolean(getHotelIdFromAccommodationPage())

  if (isAllOffersOpen) {
    return getTopOffersSearchId(state)
  }

  if (isAccommodationPage) {
    return getTopOffersSearchId(state)
  }

  return getSearchId(state)
}

/**
 * Returns searchId from either SAPI.search or SAPI.offersSearch, depends on the exp
 * @param state - App root state
 * @returns search id
 */
export const getSearchIdFromConditionalSource = state => {
  const sapiSearchId = getSearchId(state)
  const currentSearchId = getCurrentOffersSearchId(state)
  const searchId = currentSearchId || sapiSearchId

  return searchId
}

/**
 * Override some properties of the analytics SearchContext for searchApi fetches
 */
export const getOffersSearchContext = (state: RootState) => {
  const {rooms, checkIn, checkOut} = getSearchParameters(state)
  return {
    ...getCommonSearchParams(state),
    rooms,
    checkIn,
    checkOut,
    searchId: getCurrentOffersSearchId(state),
    anchorHotelId: getAccommodationPageHotelId(state),
    isNoDates: false,
    isDefaultDates: false,
    placeId: null,
    isOffersOnlyFetch: true
  }
}

/**
 * Apply the override when relevant
 */
export const getSearchContextOffersOnlySearchOverride = (state: RootState) => {
  const isAccommodationPage = Boolean(getHotelIdFromAccommodationPage())

  if (isAccommodationPage) {
    const isUsingHotelApi = Boolean(getTopOffersSearchId(state))
    return isUsingHotelApi ? getOffersSearchContext(state) : {}
  }

  return {}
}

/**
 * Returns true if rooms list is empty.
 * Returns false when rooms is fetching or loading
 * Returns false when rooms are not empty
 */
export const getIsRoomsResultEmpty = createSelector(
  [getRoomsResult],
  result => {
    const {data} = result || {}
    const {rooms, splitBooking, status} = data || {}

    return rooms?.length === 0 && !splitBooking && status?.complete === true
  }
)

/**
 * Returns true if rooms are polling and there are no results yet
 * as soon as there are results, it will return false
 */
export const getIsRoomsPollingWaitingForResults = createSelector(
  [getRoomsResult],
  result => {
    const {data} = result || {}
    const {rooms, status} = data || {}

    return rooms?.length === 0 && status?.complete === false
  }
)

/**
 * Returns true when rooms polling is complete
 */
export const getIsRoomsPollingComplete = createSelector(
  [getRoomsResult],
  result => {
    const {data} = result || {}
    const {status} = data || {}

    return status?.complete === true
  }
)

/**
 * Returns true when the given offer is the first (top-positioned) offer
 * from the unfiltered rooms result.
 */
const getIsTopRoomOffer = createSelector(
  [getRoomsResult, (_, offerId: string) => offerId],
  (result, offerId) => {
    if (!offerId) return false
    return result?.data?.rooms?.[0]?.offers?.[0]?.id === offerId
  }
)

/**
 * Returns true when the given regular offer is the cheapest by total price.
 * Returns false when split booking is cheapest or no regular offers exist.
 */
export const getIsCheapestRoomOfferByTotalPrice = createSelector(
  [getCheapestRegularOfferFromSortedEntities, (_, offerId: string) => offerId],
  (cheapestOffer, offerId) => {
    if (!offerId) return false
    if (!cheapestOffer) return false
    return cheapestOffer.id === offerId
  }
)

/**
 * Returns true when the cheapest room offer is cheaper than or equal to the
 * cheapest top offer (provider/OTA offer from the offer box).
 * When no top offers exist, defaults to true (room offer is cheapest).
 */
export const getIsRoomOfferCheapestOverall = createSelector(
  [
    getStateParam,
    getAccommodationPageHotelId,
    getCheapestRegularOfferFromSortedEntities
  ],
  (state, hotelId, cheapestRoomOffer) => {
    if (!cheapestRoomOffer) return false

    const topOffers = getHotelOfferEntityOffers(state, hotelId)
    if (!topOffers?.length) return true

    const cheapestTopOffer = getCheapestTopOfferByNightlyRate(topOffers)
    if (!cheapestTopOffer) return true

    const roomOfferTotalPrice = getTotalPrice(cheapestRoomOffer.prices[0])
    const topOfferTotalPrice = calculateTotalRate(
      cheapestTopOffer.rate,
      true,
      true
    )

    return roomOfferTotalPrice <= topOfferTotalPrice
  }
)

/**
 * Returns true rooms result are not fetched yet
 */
export const getIsRoomsQueryLoading = createSelector(
  [getRoomsResult],
  result => result.status !== QueryStatus.fulfilled
)

export const getIsFilteredRoomsResultEmpty = createSelector(
  [getFilteredRoomsData],
  result => {
    const {rooms, status, splitBooking} = result || {}

    return rooms?.length === 0 && !splitBooking && status?.complete === true
  }
)

/**
 * Late split-booking signal: rooms result's primary entity (clicked or
 * cheapest-after-sort) is a split booking. Mirrors the primary-entity
 * computation in `usePriceTrend.ts`. Returns false while rooms are still
 * loading/polling so consumers don't act on incomplete data.
 */
export const getIsRoomsPrimaryOfferSplitBooking = createSelector(
  [
    getFilteredRoomsData,
    getIsRoomsQueryLoading,
    getIsRoomsPollingWaitingForResults
  ],
  (filteredData, isLoading, isWaitingForResults) => {
    if (isLoading || isWaitingForResults) return false
    return Boolean(filteredData?.splitBooking)
  }
)

/**
 * Combined split-booking signal for the v3 PriceTrend gate. Anchor side
 * reuses `hasPrimarySplitBookingOffer` from `getSplitBookingDetails` — the
 * same signal `BottomCTA` uses to decide whether to render the SB card —
 * so allocation stays in lockstep with what users actually see. This covers
 * all three anchor-stage promotion paths (`IsYourChoice` tag, `TheCheapest`
 * tag, and the 10% promotion rule) rather than just `IsYourChoice`. Rooms
 * side catches the residual <2% cheaper SB case where no anchor rule fires
 * but rooms sort still ranks SB first.
 */
export const getIsPrimaryOfferSplitBookingForPriceInsights = createSelector(
  [
    (state: RootState) =>
      getSplitBookingDetails(state, getAccommodationPageHotelId(state)),
    getIsRoomsPrimaryOfferSplitBooking
  ],
  ({hasPrimarySplitBookingOffer}, fromRooms) =>
    hasPrimarySplitBookingOffer || fromRooms
)

export const getCommonPaymentPageLandingParams = createSelector(
  [
    getMeta,
    getShowTotalPrices,
    getLandingTrafficSource,
    getSearchIdFromConditionalSource
  ],
  (meta, showTotalPrices, landingTrafficSource, searchId) => {
    //prepare SRP clicked offer/sb parameters if available
    const srpSelectedOffer = getRoomsSearchParamsFromApUrl()
    const selectedOfferParams = {
      ...(srpSelectedOffer?.clickedOfferId
        ? {oid: srpSelectedOffer?.clickedOfferId}
        : {})
    }

    /**
     * If it's hotel-card clicker scenario (no redirectId in the url)
     * we generate a new one for the entire AP stay,
     * should be memoized on the selector level
     */
    const redirectId = getApLandingRedirectId()

    // Persist rate checker params from AP to PP (handles both direct and label-encoded)
    const urlParams = parseQueryString(location.search)
    const gsite = getGsiteValueFromUrl(urlParams)
    const vf = getVfValueFromUrl(urlParams)

    return {
      //add SRP clicked offer/sb parameters
      ...selectedOfferParams,

      //add generic app state params
      anonymousId: meta.anonymousId,
      utm_source: landingTrafficSource,
      searchId,
      showTotalPrices: showTotalPrices ? '1' : '0',
      isDirectToPayment: '1',
      redirectId,
      ...(gsite && {gsite}),
      ...(vf && {vf})
    }
  }
)

const makeGetAppLockedDealConfigByIndex = (index: number) =>
  createSelector(
    [getStateParam, getAccommodationPageHotelId],
    (state, hotelId) => {
      const hotelOffers = getHotelOfferEntityOffers(state, hotelId)
      const offer = hotelOffers?.[index]
      return (
        isBofhOffer(offer) &&
        getOfferAccessFlowSettings(state, hotelId, offer?.id).isAppRequired
      )
    }
  )

export const getRoomOfferAppLockedConfig = createSelector(
  [
    getDeviceLayout,
    getIsTopRoomOffer,
    makeGetAppLockedDealConfigByIndex(0),
    makeGetAppLockedDealConfigByIndex(1),
    makeGetAppLockedDealConfigByIndex(2)
  ],
  (
    deviceLayout,
    isTopRoomOffer,
    isTopOfferAppLocked,
    isSecondOfferAppLocked,
    isThirdOfferAppLocked
  ) => {
    const isDesktop = deviceLayout === 'desktop'

    const isAppLockedDeal =
      isTopOfferAppLocked || isSecondOfferAppLocked || isThirdOfferAppLocked
    const isRoomOfferAppLocked = isAppLockedDeal && isTopRoomOffer
    if (!isRoomOfferAppLocked) return {isRoomOfferAppLocked: false}

    const appDownloadLinkId = isDesktop
      ? Placement.WebToAppDesktopAccommodationRoomOffer
      : Placement.AccommodationRoomOffer
    return {isRoomOfferAppLocked: true, appDownloadLinkId}
  }
)

export const getRoomOfferById = createSelector(
  [getOfferIdParam, getRoomsResult],
  (offerId, roomsResult) => {
    const {data} = roomsResult || {}
    const {rooms} = data || {}
    if (!rooms) return null
    return rooms
      .flatMap(room => room.offers)
      .find(offer => offer.id === offerId)
  }
)

export const getIsRoomOfferAuthLocked = createSelector(
  [getStateParam, getRoomOfferById],
  (state, roomOffer) => {
    if (!roomOffer) return false
    return getIsLockedDeal(roomOffer)(state)
  }
)

export const getRoomOfferAccessConfig = createSelector(
  [getRoomOfferAppLockedConfig, getIsRoomOfferAuthLocked],
  ({isRoomOfferAppLocked: isAppLocked}, isAuthLocked): OfferAccessFlow => {
    if (isAppLocked) return OfferAccessFlow.AppLocked
    if (isAuthLocked) return OfferAccessFlow.PrivateLockedDeal
    return OfferAccessFlow.RegularDeal
  }
)

const getHotelSelector = createSelector([getHotelIdParam], (hotelId: string) =>
  searchApi.endpoints.getHotel.select({hotelId})
)

export const getHotelResult = createSelector(
  [getStateParam, getHotelSelector],
  (state: RootState, hotelSelector) => hotelSelector(state)
)

export const getHotelHasIsraeliVat = createSelector(
  [getHotel, getHotelResult],
  (hotelEntity, hotelResult) => {
    const hotel = hotelResult?.data || hotelEntity
    return getHasILResidentVAT(hotel?.country, hotel?.navPathInfo?.city?.id)
  }
)

export const getFAQData = createSelector(
  [getStateParam, getHotelIdParam],
  (state: RootState, hotelId: string) =>
    searchApi.endpoints.getFAQ.select({hotelId})(state)?.data
)

export const getHotelRoomsData = createSelector(
  [getStateParam, getHotelIdParam],
  (state: RootState, hotelId: string) =>
    searchApi.endpoints.getHotelRooms.select({hotelId})(state)?.data?.rooms ??
    EMPTY_ROOMS
)

export const getHotelRoomById = createSelector(
  [getHotelRoomsData, getRoomIdParamAfterHotelId],
  (rooms, roomId) => rooms?.find(room => room.id === roomId)
)

export const getRoomById = createSelector(
  [getRoomIdParam, getFilteredRoomsData],
  (roomId, roomsResult) => {
    const {rooms = []} = roomsResult || {}
    return rooms.find(room => room.id === roomId)
  }
)
export const getAccommodationPageHotel = (state: RootState) => {
  const hotelId = getAccommodationPageHotelId(state)
  return getHotelResult(state, hotelId)?.data
}

export const getAccommodationPageHotelTimezone = createSelector(
  [getAccommodationPageHotel],
  hotel => hotel?.timezone
)

export const getAccommodationPageHotelCountry = createSelector(
  [getAccommodationPageHotel],
  hotel => hotel?.country
)

export const getShowHowToCancelLink = createSelector(
  [getUserCountryCode, getAccommodationPageHotelCountry],
  (userCountryCode, hotelCountry) =>
    userCountryCode === 'IL' && (!hotelCountry || hotelCountry === 'IL')
)

/**
 * Checks if PriceTrend should be shown based on whether the primary offer changed due to filtering.
 *
 * Compares the cheapest offer from unfiltered rooms data with filtered rooms data.
 * Since PriceTrend's chart shows historical cheapest prices, showing it with
 * a filtered (more expensive) offer would be misleading.
 *
 * Returns true if:
 * - The filtered cheapest offer matches the unfiltered cheapest offer
 *   (filter didn't change which offer is primary)
 *
 * Returns false if:
 * - Either offer data is missing (data not loaded yet)
 * - The filtered cheapest offer is different from the unfiltered cheapest offer
 *   (filter changed the primary offer, hide PriceTrend)
 *
 * Note: Returning false during loading prevents a flash where PriceTrend appears
 * briefly before being hidden once data loads and reveals a filter changed the offer.
 */
export const getIsPrimaryOfferCheapest = createSelector(
  [getRoomsResult, getFilteredRoomsData],
  (unfilteredResult, filteredData) => {
    const unfilteredRooms = unfilteredResult?.data?.rooms ?? []
    const filteredRooms = filteredData?.rooms ?? []

    // Get cheapest offer from unfiltered rooms (first room's first offer)
    const cheapestUnfilteredOffer = unfilteredRooms[0]?.offers?.[0]
    // Get cheapest offer from filtered rooms
    const cheapestFilteredOffer = filteredRooms[0]?.offers?.[0]

    // If either offer is missing (data not loaded yet), return false to prevent
    // PriceTrend from flashing if filters will change the primary offer once loaded.
    if (!cheapestUnfilteredOffer || !cheapestFilteredOffer) {
      return false
    }

    // Compare by offer ID - if they match, the filter didn't change the cheapest offer
    return cheapestUnfilteredOffer.id === cheapestFilteredOffer.id
  }
)
